import config from '../../config';
import { getIframeCommunicationScript } from './iframeCommunicationScript';
import { downloadAsFile } from '@utils';
import { allowedFileExtensions } from '@features/appRun/utils/renderResults';
import { utf8ToBase64 } from '../../apiHooks/utils';
import { IFileInIframe } from './iframe.types';

export function isFilePathGzippedText(filePath: string): boolean {
    const filePathLowered = filePath.toLowerCase();
    return ['.pdb.gz', '.cif.gz', '.mmcif.gz'].some((structureExtension) =>
        filePathLowered.endsWith(structureExtension),
    );
}

export function isIframeSandboxSupported(): boolean {
    const tmpIframe = document.createElement('iframe');
    const sandboxSupported = 'sandbox' in tmpIframe;
    tmpIframe.remove();
    return sandboxSupported;
}

interface IOptions {
    file: IFileInIframe | null;
    iframeId: string;
    job?: { uuid: string; auth_token: string; app_uri: string; created_at: string };
    user?: { email: string; handle: string };
    presignedStorageUrl?: string;
    growHeightToFitContent?: boolean;
    enableDebouncedAutoResize?: boolean;
    centerImage?: boolean;
}

export async function getHtmlRendererParts(options: IOptions): Promise<Uint8Array[]> {
    const { iframeId, file, presignedStorageUrl, job, user, growHeightToFitContent, enableDebouncedAutoResize, centerImage } = options;

    let htmlRendererPath: string;

    if (!file) {
        htmlRendererPath = config.fileRendererHtmlPaths.generic;
    } else {
        const hasStructureExtension = [
            '.bcif.gz',
            '.bcif',
            '.cif.gz',
            '.cif',
            '.mmcif.gz',
            '.mmcif',
            '.pdb.gz',
            '.pdb',
        ].some((structureExtension) => file.path.toLowerCase().endsWith(structureExtension));

        if (file.path.toLowerCase().endsWith('.html') && !file.viewRaw) {
            htmlRendererPath = `/proxy/files/v1/data/?upstream_url_base64=${utf8ToBase64(presignedStorageUrl)}&path_base64=${utf8ToBase64(file.path)}`;
        } else if (hasStructureExtension && !file.viewRaw) {
            htmlRendererPath = config.fileRendererHtmlPaths.structure;
        } else {
            htmlRendererPath = config.fileRendererHtmlPaths.generic;
        }
    }

    const response = await fetch(`${location.origin}${htmlRendererPath}`);
    if (response.status !== 200) {
        throw new Error(`Failed to fetch HTML renderer. Got HTTP status: ${response.status}`);
    }

    const data = await response.arrayBuffer();
    const rendererHtmlByteArray = new Uint8Array(data);
    const communicationScriptHtmlByteArray = getIframeCommunicationScript({ iframeId, file, job, user, growHeightToFitContent, enableDebouncedAutoResize, centerImage });

    return [communicationScriptHtmlByteArray, rendererHtmlByteArray];
}

export async function getHtmlRendererObjectUrl(options: IOptions): Promise<string> {
    return getHtmlRendererObjectUrlFromParts(await getHtmlRendererParts(options));
}

export function getHtmlRendererObjectUrlFromParts(htmlParts: Uint8Array[]): string {
    const htmlBlob = new Blob(htmlParts, { type: 'text/html' });
    return URL.createObjectURL(htmlBlob);
}

// The parts are transferred to the renderer iframe, which decodes them itself, so the HTML is never
// copied into a string here. A part that is a view into a larger buffer is copied as transferring
// would hand over the entire buffer.
export function getTransferableHtmlBuffers(htmlParts: Uint8Array[]): ArrayBuffer[] {
    return htmlParts.map((part) =>
        part.byteOffset === 0 && part.byteLength === part.buffer.byteLength
            ? (part.buffer as ArrayBuffer)
            : (part.slice().buffer as ArrayBuffer),
    );
}

export function getResultRendererIframeOrigin(hostname: string): string {
    const randomString = crypto.randomUUID().replace(/-/g, '');
    return `https://bootstrap-${randomString}.${hostname}`;
}

export function downloadFile(presignedStorageUrl: string, filePath: string): void {
    const fileName = filePath.split('/').pop();
    const url = `${location.origin}/proxy/files/v1/data/?upstream_url_base64=${utf8ToBase64(presignedStorageUrl)}&path_base64=${utf8ToBase64(filePath)}`;
    const fileExtension = fileName.toLowerCase().split('.').pop();
    const disallowedFileExtensions = ['exe'];
    const downloadToFilename =
        !disallowedFileExtensions.includes(fileExtension) &&
        (config.isEnterprise || allowedFileExtensions.includes(fileExtension))
            ? fileName
            : `${fileName}.data`;
    downloadAsFile(url, downloadToFilename);
}

export function isFileExtensionRichAndRawRenderable(filePath: string): boolean {
    if (isFilePathGzippedText(filePath)) {
        return true;
    }

    const richAndRawRenderFileExtensions: string[] = ['cif', 'csv', 'json', 'md', 'mmcif', 'pdb', 'tsv'];
    const extension = filePath.toLowerCase().split('.').pop();
    return richAndRawRenderFileExtensions.includes(extension);
}

export async function getJobInputFileData(
    job: { uuid: string; auth_token: string; app_uri: string },
    path: string,
    parseGz: boolean,
): Promise<Uint8Array> {
    const response = await fetch(
        `${location.origin}/api/proxy/files/jobs/${job.uuid}/input/data/`
            .concat(`?path_base64=${utf8ToBase64(path)}`)
            .concat(parseGz ? '&parse_gz=true' : ''),
        {
            headers: { 'Job-Auth-Token': job.auth_token },
        },
    );
    if (response.status === 200) {
        const arrayBuffer = await response.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    }
    throw Error(`Response status ${response.status}`);
}
